import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  LayoutDashboard,
  Mail,
  FileText,
  Calendar,
  ToggleLeft,
  KeyRound,
  LogOut,
  Home,
  Menu,
  X,
  Users,
  CalendarCheck,
} from "lucide-react";
import LoginForm from "./LoginForm";
import ChangePasswordModal from "./ChangePasswordModal";
import WaitlistPanel from "./WaitlistPanel";
import PapersPanel from "./PapersPanel";
import EventsPanel from "./EventsPanel";
import VisibilityPanel from "./VisibilityPanel";
import VisitorsPanel from "./VisitorsPanel";
import AIWeekPanel from "./AIWeekPanel";
import { TOKEN_KEY } from "./adminApi";

const NAV = [
  { key: "visitors", label: "Visitors", icon: Users },
  { key: "aiweek", label: "AI Week 2026", icon: CalendarCheck },
  { key: "waitlist", label: "Waitlist", icon: Mail },
  { key: "papers", label: "Publications", icon: FileText },
  { key: "events", label: "Events", icon: Calendar },
  { key: "visibility", label: "Sections", icon: ToggleLeft },
];

const AdminDashboard = () => {
  const [authed, setAuthed] = useState(!!sessionStorage.getItem(TOKEN_KEY));
  const [active, setActive] = useState("visitors");
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  if (!authed) {
    return <LoginForm onSuccess={() => setAuthed(true)} />;
  }

  return (
    <div className="min-h-screen bg-[#F4EEDF] flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-64 bg-gray-900 text-white flex flex-col transition-transform lg:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="px-6 py-6 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-white/50 mb-1">
              <LayoutDashboard className="w-3 h-3" />
              Admin
            </div>
            <div className="text-lg font-light tracking-tight">K/AI</div>
          </div>
          <button
            onClick={() => setMobileOpen(false)}
            className="lg:hidden text-white/70 hover:text-white"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            const isActive = active === item.key;
            return (
              <button
                key={item.key}
                onClick={() => {
                  setActive(item.key);
                  setMobileOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-white/10 text-white"
                    : "text-white/70 hover:text-white hover:bg-white/5"
                }`}
                data-testid={`admin-nav-${item.key}`}
              >
                <Icon className="w-4 h-4" />
                {item.label}
                {isActive && (
                  <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#00BCD4]" />
                )}
              </button>
            );
          })}
        </nav>

        <div className="border-t border-white/10 p-3 space-y-1">
          <Link
            to="/"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors"
            data-testid="admin-back-home"
          >
            <Home className="w-4 h-4" />
            View website
          </Link>
          <button
            onClick={() => setShowChangePwd(true)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors"
            data-testid="admin-change-pwd-btn"
          >
            <KeyRound className="w-4 h-4" />
            Change password
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setMobileOpen(true)}
          className="text-gray-600 hover:text-gray-900"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500">
          K/AI Admin
        </div>
        <div className="w-5" />
      </div>

      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 lg:ml-0 pt-16 lg:pt-0 min-w-0">
        <div className="max-w-6xl mx-auto p-6 sm:p-10">
          {active === "visitors" && <VisitorsPanel />}
          {active === "aiweek" && <AIWeekPanel />}
          {active === "waitlist" && <WaitlistPanel />}
          {active === "papers" && <PapersPanel />}
          {active === "events" && <EventsPanel />}
          {active === "visibility" && <VisibilityPanel />}
        </div>
      </main>

      {showChangePwd && (
        <ChangePasswordModal
          onClose={() => setShowChangePwd(false)}
          onSuccess={() => {
            setShowChangePwd(false);
            handleLogout();
          }}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
